import { axiosSecure } from './api'
import { Logger } from '../logger'

/**
 * ADAS Intelligence Scrubber API wrappers.
 * Backend exposes the endpoints under /v1/scrub-jobs and /v1/scrub-estimates.
 */

/** POST /v1/scrub-jobs/upload — multipart upload of an estimate PDF.
 *  The backend expects calibration_center_id as a query parameter (Spring @RequestParam Long). */
export const uploadScrubJob = async ({ file, calibrationCenterId }) => {
  try {
    const form = new FormData()
    form.append('file', file)
    // customErrorHandling skips the global response interceptor so HTTP 429 (per-shop
    // upload rate limit) surfaces to the caller's try/catch — the upload modal renders
    // the specific "Too many uploads" copy inline instead of a generic global toast.
    const response = await axiosSecure.post('/v1/scrub-jobs/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { calibration_center_id: calibrationCenterId },
      customErrorHandling: true
    })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'uploadScrubJob', fileName: file?.name, calibrationCenterId } })
    throw error
  }
}

/** GET /v1/scrub-jobs/{id} — poll for job status after upload. */
export const getScrubJobStatus = async (jobId) => {
  try {
    // customErrorHandling keeps poll failures out of the global toast; the upload flow
    // surfaces them inline, and terminal-but-failed jobs are reported via failureCode
    // on a 200 response rather than an HTTP error.
    const response = await axiosSecure.get(`/v1/scrub-jobs/${jobId}`, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'getScrubJobStatus', jobId } })
    throw error
  }
}

/** GET /v1/scrub-estimates — paginated list with optional filters.
 *  ccId scopes results to the caller's active calibration center. Non-admin callers
 *  who omit it fall back to their JWT primary center; admins fall back to a global
 *  view. Pass an unauthorised ccId → 401 (handled by the global auth interceptor). */
export const listScrubEstimates = async ({
  page = 0,
  size = 50,
  client,
  roNumber,
  vin,
  vehicle,
  status,
  ccId,
  dateFrom,
  dateTo,
  sortBy,
  sort
} = {}) => {
  try {
    const params = { page, size }
    if (ccId != null) params.ccId = ccId
    if (client) params.client = client
    if (roNumber) params.roNumber = roNumber
    if (vin) params.vin = vin
    if (vehicle) params.vehicle = vehicle
    if (status) params.status = status
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo
    // Column sort (whitelisted by the backend); omitted -> most recently received first.
    if (sortBy) {
      params.sortBy = sortBy
      params.sort = sort || 'DESC'
    }
    const response = await axiosSecure.get('/v1/scrub-estimates', { params })
    return response.data
  } catch (error) {
    Logger.error({
      message: error,
      payload: {
        file: 'scrubApi',
        method: 'listScrubEstimates',
        page,
        size,
        client,
        roNumber,
        vin,
        vehicle,
        status,
        ccId,
        dateFrom,
        dateTo
      }
    })
    throw error
  }
}

/** GET /v1/scrub-estimates/detail/{id} — full detail including analytical summary. */
export const getScrubEstimateDetail = async (id) => {
  try {
    const response = await axiosSecure.get(`/v1/scrub-estimates/detail/${id}`)
    return response.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'getScrubEstimateDetail', id } })
    throw error
  }
}

/** PATCH /v1/scrub-estimates/detail/{id} — update client_name / insurer_name on the existing
 *  estimate version (no version bump). Body keys are snake_case per the contract; null/omitted
 *  fields are left unchanged, "" clears them. The calibration_center_id is included alongside
 *  the patched fields to match the auth pattern used across the new scrub-estimates APIs.
 *  Returns the full updated detail DTO (same shape as GET /detail/{id}). */
export const patchScrubEstimateDetail = async (id, { calibration_center_id, ...rest }) => {
  try {
    const body = { calibration_center_id, ...rest }
    const response = await axiosSecure.patch(`/v1/scrub-estimates/detail/${id}`, body, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'patchScrubEstimateDetail', id } })
    const serverMsg = error?.response?.data?.message || error?.response?.data
    throw new Error(typeof serverMsg === 'string' ? serverMsg : error?.message || 'Failed to update estimate')
  }
}

/** GET /v1/scrub-estimates/{roNumber} — latest estimate for an RO number. */
export const getLatestScrubEstimateByRo = async (roNumber, calibrationCenterId) => {
  try {
    const response = await axiosSecure.get(`/v1/scrub-estimates/${roNumber}`, {
      params: { calibration_center_id: calibrationCenterId }
    })
    return response.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'getLatestScrubEstimateByRo', roNumber, calibrationCenterId } })
    throw error
  }
}

/** GET /v1/scrub-estimates/{roNumber}/history — all versions for an RO. */
export const getScrubEstimateHistory = async (roNumber, calibrationCenterId) => {
  try {
    const response = await axiosSecure.get(`/v1/scrub-estimates/${roNumber}/history`, {
      params: { calibration_center_id: calibrationCenterId }
    })
    return response.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'getScrubEstimateHistory', roNumber, calibrationCenterId } })
    throw error
  }
}

/** PATCH /v1/scrub-estimates/{id}/review — mark estimate as REVIEWED. */
export const markScrubEstimateReviewed = async (id) => {
  try {
    const response = await axiosSecure.patch(`/v1/scrub-estimates/${id}/review`)
    return response.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'markScrubEstimateReviewed', id } })
    throw error
  }
}

/** POST /v1/scrub-estimates/{roNumber}/calibrations — ADD or REMOVE a calibration.
 *  customErrorHandling skips the global axios response interceptor so failures surface
 *  to the caller's try/catch instead of being swallowed by the default toast handler.
 *  calibration_center_id is included in the body for auth/consistency with the rest of
 *  the scrub-estimates APIs. */
export const upsertScrubCalibration = async (roNumber, { calibration_center_id, ...rest }) => {
  try {
    const response = await axiosSecure.post(`/v1/scrub-estimates/${roNumber}/calibrations`, rest, {
      params: { calibration_center_id },
      customErrorHandling: true
    })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'upsertScrubCalibration', roNumber } })
    const serverMsg = error?.response?.data?.message || error?.response?.data
    throw new Error(typeof serverMsg === 'string' ? serverMsg : error?.message || 'Failed to update calibration')
  }
}

/** PATCH /v1/scrub-estimates/{id}/quote-sent — mark estimate as QUOTE_SENT. */
export const markScrubEstimateQuoteSent = async (id) => {
  try {
    const response = await axiosSecure.patch(`/v1/scrub-estimates/${id}/quote-sent`)
    return response.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'markScrubEstimateQuoteSent', id } })
    throw error
  }
}

const parseContentDispositionFilename = (disposition) => {
  if (!disposition) return null
  // RFC 5987 (filename*=UTF-8''…) takes precedence when present, otherwise plain filename=…
  const utf8 = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition)
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].replace(/^"|"$/g, ''))
    } catch {
      /* fall through to plain match */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1] ?? null
}

/** Shared filename composer for the RO-keyed download naming scheme:
 *    {prefix}_RO{RO}_{Make}.pdf
 *  Each segment is dropped when its source value is missing or is the em-dash
 *  placeholder, so the name degrades gracefully:
 *    {prefix}_RO{RO}.pdf            (no make)
 *    {prefix}_{last6VIN}_{Make}.pdf (no RO)
 *    {prefix}_{Make}.pdf            (no RO, unusable VIN)
 *    {prefix}.pdf                   (nothing to key on) */
const buildRoFilename = (prefix, { make, ro, vin } = {}) => {
  const cleanRo = String(ro ?? '')
    .trim()
    .replace(/[^0-9A-Za-z]+/g, '')
  const cleanVin = String(vin ?? '').replace(/[^0-9A-Za-z]/g, '')
  // Spaces / punctuation in the make become hyphens: "Land Rover" -> "Land-Rover"
  const cleanMake = String(make ?? '')
    .trim()
    .replace(/[^0-9A-Za-z]+/g, '-')
    .replace(/^-+|-+$/g, '')

  let key = ''
  if (cleanRo) key = `RO${cleanRo}`
  else if (cleanVin.length >= 6) key = cleanVin.slice(-6)

  return `${[prefix, key, cleanMake].filter(Boolean).join('_')}.pdf`
}

/** ADAS Intelligence report PDF filename: AI_Report_RO{RO}_{Make}.pdf */
export const buildAdasReportFilename = ({ make, ro, vin } = {}) => buildRoFilename('AI_Report', { make, ro, vin })

/** Original uploaded collision-repair estimate filename: Estimate_RO{RO}_{Make}.pdf */
export const buildEstimateFilename = ({ make, ro, vin } = {}) => buildRoFilename('Estimate', { make, ro, vin })

const triggerBrowserDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** GET /v1/intelligence/rules — paginated calibration-rule listing with optional filters.
 *  All filter params are optional; the BE matches make/model/oem/etc. case-insensitively
 *  and caps size at 500. `active` defaults to true on the BE (soft-deleted rules hidden).
 *  Returns a Spring Page<CalibrationRuleRecordDto>: { content, totalElements, totalPages,
 *  number, size, ... }. Enum fields (operation, adasCategory, calibrationMethod,
 *  confidence, productType) serialize as display labels (e.g. "R&R", "Front Radar"). */
export const listCalibrationRules = async ({
  page = 0,
  size = 20,
  make,
  model,
  oem,
  adasCategory,
  operation,
  sectionHeader,
  active
} = {}) => {
  try {
    const params = { page, size }
    if (make) params.make = make
    if (model) params.model = model
    if (oem) params.oem = oem
    if (adasCategory) params.adasCategory = adasCategory
    if (operation) params.operation = operation
    if (sectionHeader) params.sectionHeader = sectionHeader
    if (active != null) params.active = active
    const response = await axiosSecure.get('/v1/intelligence/rules', { params })
    return response.data
  } catch (error) {
    Logger.error({
      message: error,
      payload: {
        file: 'scrubApi',
        method: 'listCalibrationRules',
        page,
        size,
        make,
        model,
        oem,
        adasCategory,
        operation,
        sectionHeader,
        active
      }
    })
    throw error
  }
}

/** Shared error unwrap for the rule-maintenance CRUD calls: the BE returns 400 with an
 *  ErrorMessage body ({ message }) for validation failures (missing required fields,
 *  duplicate rule_id, bad years), which callers surface inline in the dialog. */
const throwRuleMaintenanceError = (error, fallback) => {
  const serverMsg = error?.response?.data?.message || error?.response?.data
  throw new Error(typeof serverMsg === 'string' ? serverMsg : error?.message || fallback)
}

/** POST /v1/intelligence/rules — create a single rule. The payload mirrors
 *  CalibrationRuleRequestDto (all camelCase strings; modelYearStart/End are 4-digit
 *  year strings). Required by the BE: ruleId ([A-Z]{2,4}-\d{4}), oem, make, partName,
 *  operation, calibrationSystem, calibrationMethod, confidence, productType, and both
 *  years. Runs the same validation as the xlsx upload; returns the created record. */
export const createCalibrationRule = async (payload) => {
  try {
    const response = await axiosSecure.post('/v1/intelligence/rules', payload, { customErrorHandling: true })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'createCalibrationRule', ruleId: payload?.ruleId } })
    throwRuleMaintenanceError(error, 'Failed to create rule')
  }
}

/** PUT /v1/intelligence/rules/{ruleId} — update a rule (writes a change-log entry).
 *  The body's ruleId must match the path (or be omitted); returns the updated record. */
export const updateCalibrationRule = async (ruleId, payload) => {
  try {
    const response = await axiosSecure.put(`/v1/intelligence/rules/${encodeURIComponent(ruleId)}`, payload, {
      customErrorHandling: true
    })
    return response?.data
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'updateCalibrationRule', ruleId } })
    throwRuleMaintenanceError(error, 'Failed to update rule')
  }
}

/** DELETE /v1/intelligence/rules/{ruleId}?reason=… — soft delete. `reason` is a
 *  required query param on the BE and lands in the rule's change history. */
export const deleteCalibrationRule = async (ruleId, reason) => {
  try {
    await axiosSecure.delete(`/v1/intelligence/rules/${encodeURIComponent(ruleId)}`, {
      params: { reason },
      customErrorHandling: true
    })
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'deleteCalibrationRule', ruleId } })
    throwRuleMaintenanceError(error, 'Failed to delete rule')
  }
}

/** GET /v1/intelligence/adas-category-mappings — paginated ChromeData→AdasCategory mapping
 *  listing. Filters: matchType ("UNIQUE_KEY" | "FEATURE_NAME"), adasCategory (enum NAME,
 *  e.g. "SURROUND_VIEW_CAMERA" — exact match, unlike the rules API which uses labels),
 *  key (case-insensitive contains on matchKey), active (BE defaults to true). Size caps
 *  at 500. Returns a Spring Page<AdasCategoryMappingDto>. */
export const listAdasCategoryMappings = async ({ page = 0, size = 20, matchType, adasCategory, key, active } = {}) => {
  try {
    const params = { page, size }
    if (matchType) params.matchType = matchType
    if (adasCategory) params.adasCategory = adasCategory
    if (key) params.key = key
    if (active != null) params.active = active
    const response = await axiosSecure.get('/v1/intelligence/adas-category-mappings', { params })
    return response.data
  } catch (error) {
    Logger.error({
      message: error,
      payload: { file: 'scrubApi', method: 'listAdasCategoryMappings', page, size, matchType, adasCategory, key, active }
    })
    throw error
  }
}

/** GET /v1/intelligence/rules/adas-categories — distinct ADAS categories pulled from the
 *  active calibration rules. Returns a string[] used to populate the Add Calibration
 *  category dropdown; each value is both the display label and the submit value (matches
 *  CalibrationCardDto.adasCategory and the alteration API contract). */
export const listAdasCategories = async () => {
  try {
    const response = await axiosSecure.get('/v1/intelligence/rules/adas-categories')
    return Array.isArray(response.data) ? response.data : []
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'listAdasCategories' } })
    throw error
  }
}

/** GET /v1/scrub-estimates/detail/{id}/pdf — server-rendered estimate summary PDF.
 *  Returns undefined after triggering a browser download by default. Pass
 *  { asFile: true } to receive a File object instead (used by the Create Quote flow,
 *  which attaches the PDF to the new quote rather than saving it to disk).
 *
 *  When `filename` is provided, it overrides the BE's Content-Disposition value — used
 *  to enforce the FE-side AI_Report_RO{ro}.pdf naming scheme. Without it, the
 *  Content-Disposition filename wins (with `fallbackFilename` as a last resort). */
export const downloadScrubEstimatePdf = async (id, { asFile = false, filename, fallbackFilename = 'AI_Report.pdf' } = {}) => {
  try {
    const response = await axiosSecure.get(`/v1/scrub-estimates/detail/${id}/pdf`, {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' }
    })
    const resolvedName = filename || parseContentDispositionFilename(response.headers?.['content-disposition']) || fallbackFilename
    const blob = response.data
    if (asFile) return new File([blob], resolvedName, { type: 'application/pdf' })
    triggerBrowserDownload(blob, resolvedName)
    return undefined
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'downloadScrubEstimatePdf', id } })
    throw error
  }
}

/** GET /v1/scrub-estimates/detail/{id}/source-file — the ORIGINAL uploaded estimate
 *  document (raw CCC/Mitchell/Audatex PDF), as opposed to the server-rendered summary
 *  from downloadScrubEstimatePdf. Used by the Create Quote flow to seed the New Quote
 *  "Collision Repair Estimate" slot with the original file the user uploaded.
 *
 *  Mirrors downloadScrubEstimatePdf: returns a File when { asFile: true }, otherwise
 *  triggers a browser download. Re-throws so callers can handle 404 (no original
 *  retained — legacy/purged estimates) and 403 (cross-center) gracefully. */
export const downloadOriginalEstimateFile = async (id, { asFile = false, filename, fallbackFilename = 'Estimate.pdf' } = {}) => {
  try {
    const response = await axiosSecure.get(`/v1/scrub-estimates/detail/${id}/source-file`, {
      responseType: 'blob'
    })
    const resolvedName = filename || parseContentDispositionFilename(response.headers?.['content-disposition']) || fallbackFilename
    const blob = response.data
    if (asFile) return new File([blob], resolvedName, { type: blob?.type || 'application/pdf' })
    triggerBrowserDownload(blob, resolvedName)
    return undefined
  } catch (error) {
    Logger.error({ message: error, payload: { file: 'scrubApi', method: 'downloadOriginalEstimateFile', id } })
    throw error
  }
}
